import {useEffect, useState} from 'react'
import type {ReactNode} from 'react'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DialogForm} from '@/components/shared/DialogForm'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'

export interface CommentDialogProps {
    open: boolean
    title: string
    description?: ReactNode
    commentLabel: string
    commentPlaceholder?: string
    commentRequired?: boolean
    confirmLabel: string
    confirmingLabel?: string
    confirmVariant?: 'default' | 'destructive'
    confirming?: boolean
    onConfirm: (comment: string) => void
    onCancel: () => void
    'data-testid'?: string
}

const TESTID_FALLBACK = 'comment-dialog'

export function CommentDialog({
    open,
    title,
    description,
    commentLabel,
    commentPlaceholder,
    commentRequired = true,
    confirmLabel,
    confirmingLabel,
    confirmVariant = 'default',
    confirming = false,
    onConfirm,
    onCancel,
    'data-testid': testId,
}: CommentDialogProps) {
    const [comment, setComment] = useState('')
    const dialogTestId = testId ?? TESTID_FALLBACK
    /* eslint-disable i18next/no-literal-string -- testid + DOM id suffixes, not user-visible strings */
    const formTestId = `${dialogTestId}-form`
    const fieldId = `${dialogTestId}-comment`
    const inputTestId = `${dialogTestId}-input`
    /* eslint-enable i18next/no-literal-string */

    useEffect(() => {
        if (!open) setComment('')
    }, [open])

    const submitDisabled = commentRequired && comment.trim().length === 0

    function handleOpenChange(next: boolean) {
        if (!next) onCancel()
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (submitDisabled) return
        onConfirm(comment)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid={dialogTestId}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>
                <DialogForm onSubmit={handleSubmit} data-testid={formTestId}>
                    <Label htmlFor={fieldId} className="py-4">{commentLabel}</Label>
                    <Textarea
                        id={fieldId}
                        data-testid={inputTestId}
                        placeholder={commentPlaceholder}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        required={commentRequired}
                    />
                    <SaveCancelFooter
                        testIdPrefix={dialogTestId}
                        onCancel={onCancel}
                        saving={confirming}
                        disabled={submitDisabled}
                        saveLabel={confirmLabel}
                        savingLabel={confirmingLabel}
                        destructive={confirmVariant === 'destructive'}
                    />
                </DialogForm>
            </DialogContent>
        </Dialog>
    )
}
